/**
 * Denormaliza al doc de carpeta padre las banderas que el listado necesita para
 * pintar la fila sin leer `files/{id}`:
 *
 *   - `accounted`      — marca de contabilizado (ya se denormalizaba)
 *   - `hasAccounting`  — ¿tiene soporte contable adjunto? El cliente solo ve el
 *                        tag si es true (lib/accounted_visibility.js), y la
 *                        entrada del array no tiene el campo `accounting` real.
 *
 * `reference` es el doc top-level `files/{id}`. Su campo `folder` NO apunta al
 * doc de carpeta sino al doc-puntero de la subcolección (`docfolder` en
 * upload.js): dos saltos `.parent` (subcolección → doc carpeta) resuelven el
 * doc que tiene el array `files[]` (es el mismo `folderDocRef` de upload.js,
 * o sea `reference.parent` de la CollectionReference pasada a upload()).
 *
 * arrayRemove + arrayUnion en vez de sobreescribir el array completo, para no
 * pisar subidas concurrentes de otros archivos. `serverTimestamp` no es válido
 * dentro de arrays, así que se copia el Timestamp YA RESUELTO releyendo
 * `files/{id}` tras el commit principal.
 *
 * Tolerante a fallos por diseño: la escritura principal en `files/{id}` ya se
 * hizo en el batch anterior. Si esto falla (permisos, archivo pre dual-write
 * sin entrada en el array) solo se loguea — no se propaga el error.
 */

import { FILES_SUBCOLLECTION } from './folder_files';
import { hasAccounting } from './accounting_refs';

export const denormalizeFileFlagsToParent = async (reference, firebase) => {
  try {
    const fresh = await reference.get();
    const stubRef = fresh.data()?.folder;
    const folderRef = stubRef?.parent?.parent;
    if (!folderRef || typeof folderRef.get !== 'function') return;

    const resolvedAccounted = fresh.data()?.accounted || null;
    const soporte = hasAccounting(fresh.data()?.accounting);

    const folderSnap = await folderRef.get();
    const arr = folderSnap.data()?.files;
    const entry = Array.isArray(arr) ? arr.find(e => e?.fileId === reference.id) : null;

    if (entry) {
      const { accounted: _omitA, hasAccounting: _omitH, ...restEntry } = entry;
      const nextEntry = {
        ...restEntry,
        ...(resolvedAccounted ? { accounted: resolvedAccounted } : {}),
        hasAccounting: soporte,
      };
      await folderRef.update({ files: firebase().firestore.FieldValue.arrayRemove(entry) });
      await folderRef.update({ files: firebase().firestore.FieldValue.arrayUnion(nextEntry) });
      return;
    }

    // Archivo en files-store (carpeta en overflow): actualizar su doc.
    if (folderSnap.data()?.filesOverflow === true) {
      const subRef = folderRef.collection(FILES_SUBCOLLECTION).doc(reference.id);
      const subSnap = await subRef.get();
      if (subSnap.exists) {
        await subRef.update({
          accounted: resolvedAccounted ? resolvedAccounted : firebase().firestore.FieldValue.delete(),
          hasAccounting: soporte,
        });
      }
    }
    // Si no está en ninguno: archivo pre dual-write, no-op.
  } catch (e) {
    console.warn('[contabilizado] no se pudo denormalizar al padre:', e?.message);
  }
};

export default denormalizeFileFlagsToParent;
